import { motion } from "framer-motion"
import {
  RiArrowRightLine,
  RiBarChart2Fill,
  RiChat3Fill,
  RiGroup3Fill,
  RiLayoutGridFill,
  RiLink,
  RiLock2Fill,
  RiNotification3Fill,
  RiShoppingCart2Fill,
  RiSparkling2Fill,
  RiStore2Fill,
} from "@remixicon/react"
import { Link } from "react-router-dom"

import { BentoCard } from "@/components/landing/BentoCard"
import { ColorBand, ColorBandInner } from "@/components/landing/ColorBand"
import { MarketingPageShell } from "@/components/landing/MarketingPageShell"
import {
  fadeIn,
  fadeUp,
  inView,
  staggerContainer,
  staggerItem,
} from "@/components/landing/marketing-motion"
import { Button } from "@/components/ui/button"
import { useStartCta } from "@/hooks/useAppSession"

const coreFeatures = [
  {
    name: "Sales page",
    desc: "sqale.shop/yourbrand, brand, bio, shop buttons, and catalog underneath.",
    Icon: RiLink,
    variant: "lavender" as const,
    to: "/product/sales-link",
  },
  {
    name: "Products",
    desc: "Photos, price, variants, and availability customers can browse themselves.",
    Icon: RiLayoutGridFill,
    variant: "default" as const,
    to: "/product/sell",
  },
  {
    name: "Checkout",
    desc: "Order details and payment in one flow, no endless back-and-forth.",
    Icon: RiShoppingCart2Fill,
    variant: "default" as const,
    to: "/product/sell",
  },
  {
    name: "WhatsApp",
    desc: "Let buyers talk before they purchase without losing the sale.",
    Icon: RiChat3Fill,
    variant: "lime" as const,
    to: "/product/connect",
  },
  {
    name: "Orders",
    desc: "Every order in one place instead of searching through chat threads.",
    Icon: RiStore2Fill,
    variant: "default" as const,
    to: "/product/sell",
  },
  {
    name: "Customers",
    desc: "Who bought what, how often, and total spent, your buyer database.",
    Icon: RiGroup3Fill,
    variant: "default" as const,
    to: "/product/connect",
  },
  {
    name: "Analytics",
    desc: "Visits → product views → orders → revenue. Insight and Sqaleshop AI on Growth.",
    Icon: RiBarChart2Fill,
    variant: "ink" as const,
    to: "/product/grow",
  },
] as const

const growthExtras = [
  "Custom appearance & domain",
  "Advanced analytics",
  "Customer insights",
  "Abandoned order recovery",
  "WhatsApp remarketing",
  "Repeat customer tools",
  "Remove Sqaleshop branding",
] as const

const pillars = [
  {
    title: "Sell where you are",
    body: "One link works on Instagram, WhatsApp, and anywhere you paste a URL.",
    Icon: RiStore2Fill,
  },
  {
    title: "Less noise",
    body: "Orders roll into one view instead of ten chat threads.",
    Icon: RiNotification3Fill,
  },
  {
    title: "Room to grow",
    body: "Start free on your sales link; add Growth when volume demands it.",
    Icon: RiLock2Fill,
  },
] as const

export function FeaturesPage() {
  const start = useStartCta("Claim your link")

  return (
    <MarketingPageShell
      eyebrow="Features"
      title="Your sales link, with everything underneath"
      description="It's one link you share. It's a full storefront. Products, checkout, WhatsApp, orders, customers, and analytics are all under one link."
      heroVariant="lavender"
      bare
      actions={
        <>
          <Button
            size="xl"
            className="rounded-full bg-primary px-8 font-semibold text-white shadow-none hover:bg-primary/90 hover:text-white"
            asChild
          >
            <Link to={start.href} className="gap-2">
              {start.label}
              <RiArrowRightLine className="size-4" />
            </Link>
          </Button>
          <Button
            size="xl"
            variant="transparentOutline"
            className="rounded-full border-primary/25 px-8 font-semibold text-primary hover:text-primary/80"
            asChild
          >
            <Link to="/pricing">See pricing</Link>
          </Button>
        </>
      }
    >
      <ColorBand className="bg-[#f6f4ef] text-primary">
        <ColorBandInner>
          <motion.h2
            {...fadeUp}
            className="font-display max-w-3xl text-3xl leading-[0.92] sm:text-4xl lg:text-[3rem]"
          >
            Everything you need under one link
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={inView}
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {coreFeatures.map((item) => (
              <motion.div key={item.name} variants={staggerItem}>
                <Link to={item.to} className="block h-full">
                  <BentoCard variant={item.variant} hover className="h-full min-h-[220px]">
                    <span
                      className={
                        item.variant === "ink"
                          ? "inline-flex size-12 items-center justify-center rounded-2xl bg-white/15 text-white"
                          : "inline-flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"
                      }
                    >
                      <item.Icon className="size-6" />
                    </span>
                    <h3
                      className={
                        item.variant === "ink"
                          ? "font-heading mt-5 text-xl text-white"
                          : "font-heading mt-5 text-xl text-primary"
                      }
                    >
                      {item.name}
                    </h3>
                    <p
                      className={
                        item.variant === "ink"
                          ? "mt-2 text-sm font-semibold leading-relaxed text-white/75"
                          : "mt-2 text-sm font-semibold leading-relaxed text-primary/65"
                      }
                    >
                      {item.desc}
                    </p>
                  </BentoCard>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </ColorBandInner>
      </ColorBand>

      <ColorBand className="bg-[#d2e4c4] text-primary">
        <div
          className="pointer-events-none absolute -left-16 bottom-0 size-72 rounded-full bg-white/50 blur-3xl"
          aria-hidden
        />
        <ColorBandInner>
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp}>
              <div className="flex items-center gap-2 text-primary">
                <RiSparkling2Fill className="size-5" />
                <span className="font-heading text-sm uppercase tracking-wide">
                  Growth plan
                </span>
              </div>
              <h2 className="font-display mt-4 text-3xl leading-[0.92] sm:text-4xl lg:text-[3rem]">
                Unlock insight when you&rsquo;re ready
              </h2>
              <p className="mt-5 max-w-xl text-base font-semibold leading-relaxed text-primary/75 xl:text-lg">
                Analytics becomes Insight on Growth, plus Sqaleshop AI as a tab
                in your analytics dashboard.
              </p>
              <Button
                size="xl"
                className="mt-8 rounded-full bg-primary px-8 font-semibold text-white shadow-none hover:bg-primary/90 hover:text-white"
                asChild
              >
                <Link to="/pricing" className="gap-2">
                  Compare plans
                  <RiArrowRightLine className="size-4" />
                </Link>
              </Button>
            </motion.div>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={inView}
              className="grid gap-3 sm:grid-cols-2"
            >
              {growthExtras.map((item) => (
                <motion.div
                  key={item}
                  variants={staggerItem}
                  className="rounded-[1.75rem] bg-white px-5 py-4 text-sm font-semibold text-primary shadow-[0_12px_32px_-20px_rgba(69,24,146,0.18)]"
                >
                  {item}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </ColorBandInner>
      </ColorBand>

      <ColorBand className="bg-[#f6f4ef] text-primary">
        <ColorBandInner>
          <div className="grid gap-5 md:grid-cols-3">
            {pillars.map((p, i) => (
              <motion.article
                key={p.title}
                {...fadeIn}
                transition={{ ...fadeIn.transition, delay: i * 0.06 }}
                className="rounded-[2.5rem] bg-white p-7 sm:p-8"
              >
                <span className="inline-flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <p.Icon className="size-6" />
                </span>
                <h3 className="font-heading mt-5 text-xl text-primary">{p.title}</h3>
                <p className="mt-2 text-sm font-semibold leading-relaxed text-primary/65">
                  {p.body}
                </p>
              </motion.article>
            ))}
          </div>
        </ColorBandInner>
      </ColorBand>
    </MarketingPageShell>
  )
}
